import { useNavigate } from "react-router-dom";
import Footer from "../Components/Footer";
import Header from "../Components/Header";
import methodModel from "../../methods/methods";
import React, { use, useEffect, useState, useCallback } from "react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import { ProductCard, ProductSkeleton } from "../Home/Home";
import QuoteRequestModal from "../Modals/QuoteModal";
import ShareModal from "../Modals/ShareModal";
import { IoMdCall } from "react-icons/io";
import environment from "../../environment";

// ─── IndexedDB Cart Helpers with Fallback ───────────────────────────────────────────────────

const DB_NAME = environment.DB_NAME;
const STORE_NAME = environment.STORE_NAME;
const DB_VERSION = environment.DB_VERSION;

// In-memory fallback storage when IndexedDB fails
let memoryStorage = new Map();
let useMemoryFallback = false;

export function openDB() {
  return new Promise((resolve, reject) => {
    // Check if we're in a private browsing mode or if IndexedDB is unavailable
    if (useMemoryFallback) {
      reject(new Error("Using memory fallback"));
      return;
    }

    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);

      req.onupgradeneeded = (e) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "id" });
        }
      };

      req.onsuccess = () => resolve(req.result);

      req.onerror = (event) => {
        console.warn("IndexedDB error:", event.target.error);
        useMemoryFallback = true;
        reject(event.target.error);
      };
    } catch (error) {
      console.warn("IndexedDB not available:", error);
      useMemoryFallback = true;
      reject(error);
    }
  });
}

async function getCartItem(id) {
  // Use memory fallback if IndexedDB failed
  if (useMemoryFallback) {
    return memoryStorage.get(id) || null;
  }

  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(id);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  } catch (error) {
    console.warn("Falling back to memory storage for getCartItem");
    useMemoryFallback = true;
    return memoryStorage.get(id) || null;
  }
}

async function setCartItem(item) {
  // Use memory fallback if IndexedDB failed
  if (useMemoryFallback) {
    memoryStorage.set(item.id, item);
    window.dispatchEvent(new Event("cart-updated"));
    return;
  }

  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.put(item);
      req.onsuccess = () => {
        window.dispatchEvent(new Event("cart-updated"));
        resolve();
      };
      req.onerror = () => reject(req.error);
    });
  } catch (error) {
    console.warn("Falling back to memory storage for setCartItem");
    useMemoryFallback = true;
    memoryStorage.set(item.id, item);
    window.dispatchEvent(new Event("cart-updated"));
  }
}

async function removeCartItem(id) {
  // Use memory fallback if IndexedDB failed
  if (useMemoryFallback) {
    memoryStorage.delete(id);
    window.dispatchEvent(new Event("cart-updated"));
    return;
  }

  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.delete(id);
      req.onsuccess = () => {
        window.dispatchEvent(new Event("cart-updated"));
        resolve();
      };
      req.onerror = () => reject(req.error);
    });
  } catch (error) {
    console.warn("Falling back to memory storage for removeCartItem");
    useMemoryFallback = true;
    memoryStorage.delete(id);
    window.dispatchEvent(new Event("cart-updated"));
  }
}

async function getAllCartItems() {
  // Use memory fallback if IndexedDB failed
  if (useMemoryFallback) {
    return Array.from(memoryStorage.values());
  }

  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch (error) {
    console.warn("Falling back to memory storage for getAllCartItems");
    useMemoryFallback = true;
    return Array.from(memoryStorage.values());
  }
}

async function clearAllCartItems() {
  // Use memory fallback if IndexedDB failed
  if (useMemoryFallback) {
    memoryStorage.clear();
    window.dispatchEvent(new Event("cart-updated"));
    return Promise.resolve();
  }

  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.clear();
      req.onsuccess = () => {
        window.dispatchEvent(new Event("cart-updated"));
        resolve();
      };
      req.onerror = () => reject(req.error);
    });
  } catch (error) {
    console.warn("Falling back to memory storage for clearAllCartItems");
    useMemoryFallback = true;
    memoryStorage.clear();
    window.dispatchEvent(new Event("cart-updated"));
  }
}

// Function to reset corrupted database
async function resetCartDatabase() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => {
      console.log("Database deleted successfully");
      useMemoryFallback = false;
      memoryStorage.clear();
      resolve();
    };
    req.onerror = () => reject(req.error);
    req.onblocked = () => console.warn("Database delete blocked");
  });
}

// Function to check if in private browsing mode
function isPrivateBrowsing() {
  return new Promise((resolve) => {
    try {
      const db = indexedDB.open("test-private-check");
      db.onerror = () => resolve(true);
      db.onsuccess = () => {
        db.result.close();
        resolve(false);
      };
    } catch (e) {
      resolve(true);
    }
  });
}

// ─── Quantity Selector Component ─────────────────────────────────────────────

function QuantitySelector({ quantity, setQuantity, disabled }) {
  return (
    <div className="flex items-center gap-0 rounded-xl overflow-hidden border border-[#D0DFF0] bg-white shadow-sm w-fit">
      <button
        type="button"
        disabled={disabled || quantity <= 1}
        onClick={() => setQuantity(Math.max(1, quantity - 1))}
        className="w-10 h-10 flex items-center justify-center text-[#116DAE] text-lg font-bold
          hover:bg-[#EBF3FB] active:bg-[#D6E9F8] transition-colors
          disabled:opacity-30 disabled:cursor-not-allowed select-none"
        aria-label="Decrease quantity"
      >
        −
      </button>
      <div className="w-[1px] h-6 bg-[#D0DFF0]" />
      <span className="w-12 text-center text-[15px] font-semibold text-[#1a1a1a] select-none tabular-nums">
        {quantity}
      </span>
      <div className="w-[1px] h-6 bg-[#D0DFF0]" />
      <button
        type="button"
        disabled={disabled}
        onClick={() => setQuantity(quantity + 1)}
        className="w-10 h-10 flex items-center justify-center text-[#116DAE] text-lg font-bold
          hover:bg-[#EBF3FB] active:bg-[#D6E9F8] transition-colors
          disabled:opacity-30 disabled:cursor-not-allowed select-none"
        aria-label="Increase quantity"
      >
        +
      </button>
    </div>
  );
}

// ─── Add to Cart / View Cart Button ──────────────────────────────────────────

function CartButton({ inCart, onAddToCart, onViewCart, loading }) {
  if (inCart) {
    return (
      <button
        type="button"
        onClick={onViewCart}
        className="
          relative flex items-center gap-1 text-white rounded-full
          hover:scale-[1.04] duration-300 active:scale-100 transition-transform py-2 px-7
          bg-gradient-to-br from-[#20833E] to-[#42A660]
          shadow-md shadow-green-200
        "
      >
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/20">
          <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
            <path
              d="M1 5L4.5 8.5L11 1.5"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span className="font-medium">View Cart</span>
        {/* <span className="absolute -top-1 -right-1 w-3 h-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#71cd8d] opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-[#42A660] shadow-md"></span>
        </span> */}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onAddToCart}
      disabled={loading}
      className="
        flex items-center gap-1 text-white rounded-full
        hover:scale-[1.04] duration-300 active:scale-100 transition-transform py-2 px-7
        bg-gradient-to-br from-[#115B9D] to-[#116DAE]
        shadow-md shadow-blue-200
        disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100
      "
    >
      {loading ? (
        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="white"
            strokeWidth="3"
          />
          <path
            className="opacity-75"
            fill="white"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
          />
        </svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path
            d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18M16 10a4 4 0 01-8 0"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      <span className="font-medium">{loading ? "Adding…" : "Add to Cart"}</span>
    </button>
  );
}

// ─── Cart Toast Notification ──────────────────────────────────────────────────

function CartToast({ show, onHide }) {
  useEffect(() => {
    if (show) {
      const t = setTimeout(onHide, 3000);
      return () => clearTimeout(t);
    }
  }, [show, onHide]);

  return (
    <div
      className={`
        fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999]
        flex items-center gap-3 bg-white border border-[#D0DFF0]
        rounded-2xl shadow-xl px-5 py-3
        transition-all duration-300
        ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}
      `}
    >
      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EBF3FB]">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path
            d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18M16 10a4 4 0 01-8 0"
            stroke="#116DAE"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <div>
        <p className="text-sm font-semibold text-[#1a1a1a]">Added to cart!</p>
        <p className="text-xs text-gray-400">
          {useMemoryFallback
            ? "Saved for this session"
            : "Item saved successfully."}
        </p>
      </div>
      <button
        onClick={onHide}
        className="ml-2 text-gray-300 hover:text-gray-500 transition-colors"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path
            d="M18 6L6 18M6 6l12 12"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ProductDetail() {
  const Navigate = useNavigate();
  const [Quantity, setQuantity] = useState(1);
  const [ShareModalOpen, setShareModalOpen] = useState(false);
  const ProductId = methodModel.getPrams("id");
  const [ProductData, setProductData] = useState({});

  // Cart state
  const [inCart, setInCart] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);

  // Check for private browsing on mount
  useEffect(() => {
    isPrivateBrowsing().then((isPrivate) => {
      if (isPrivate) {
        console.warn("Private browsing mode detected - using memory storage");
        useMemoryFallback = true;
      }
    });
  }, []);

  useEffect(() => {
    if (ProductData?.title) {
      document.title = `${ProductData.title} | Charles Smith & Associates`;
    }
  }, [ProductData?.title]);

  useEffect(() => {
    if (!ProductId) {
      Navigate("/products");
    } else {
      // Reset state to default for the new product to prevent stale state
      setInCart(false);
      setQuantity(1);
      setProductData({});

      loader(true);
      ApiClient.get(`product/detail?id=${ProductId}`)
        .then(async (res) => {
          if (res?.success) {
            setProductData(res?.data);
            GetBrowseProducts({
              equipmentIds:
                res?.data?.equipmentTypeId?._id ||
                res?.data?.equipmentTypeId?.id,
            });

            try {
              const item = await getCartItem(ProductId);
              if (item) {
                setInCart(true);
                setQuantity(item.quantity || 1);
              } else {
                setInCart(false);
                setQuantity(1);
              }
            } catch (error) {
              console.warn("Failed to get cart item:", error);
            }
          } else {
            Navigate("/products");
          }
          loader(false);
        })
        .catch((error) => {
          console.error("Failed to fetch product:", error);
          loader(false);
          Navigate("/products");
        });
    }
  }, [ProductId]);


  const [isOpen, setIsOpen] = React.useState(false);
  const [featuredProducts, setFeaturedProducts] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const navigate = useNavigate();

  const GetBrowseProducts = (p = {}) => {
    setIsLoading(true);
    ApiClient.get("product/browse", {
      page: 1,
      count: 1000,
      ...p,
      isAiGenerated: "0",
    })
      .then((res) => {
        setFeaturedProducts(
          res.data?.products?.filter(
            (_itm) => _itm?.id != methodModel.getPrams("id"),
          ) || [],
        );
        setIsLoading(false);
      })
      .catch(() => {
        setFeaturedProducts([]);
        setIsLoading(false);
      });
  };

  const NavigateHandler = (id) => {
    window.scrollTo(0, 0);
    navigate(`/products/detail?id=${id}`);
  };

  // Add to cart handler with better error handling
  const handleAddToCart = useCallback(async () => {
    if (!ProductData?.id && !ProductData?._id) {
      console.error("Invalid product data");
      return;
    }

    setCartLoading(true);
    try {
      const cartItem = {
        id: ProductId,
        productId: ProductData?.id || ProductData?._id,
        productName: ProductData?.productName,
        image: ProductData?.image,
        model: ProductData?.model,
        modelNumber: ProductData?.modelNumber,
        manufacturerId:
          ProductData?.manufacturer || ProductData?.manufacturerId?.title,
        equipmentTypeId:
          ProductData?.equipmentType || ProductData?.equipmentTypeId?.title,
        quantity: Quantity,
        addedAt: new Date().toISOString(),
      };

      await setCartItem(cartItem);
      setInCart(true);
      setShowToast(true);
    } catch (err) {
      console.error("Cart error:", err);
      // Show user-friendly error message
      alert(
        "Unable to add item to cart. Your cart will be saved for this session only.",
      );
      // Still try to add to memory storage as fallback
      try {
        const cartItem = {
          id: ProductId,
          productId: ProductData?.id || ProductData?._id,
          productName: ProductData?.productName,
          image: ProductData?.image,
          model: ProductData?.model,
          modelNumber: ProductData?.modelNumber,
          manufacturer:
            ProductData?.manufacturer ||
            ProductData?.manufacturerId?.title ||
            "",
          equipmentType:
            ProductData?.equipmentType ||
            ProductData?.equipmentTypeId?.title ||
            "",
          quantity: Quantity,
          addedAt: new Date().toISOString(),
        };
        memoryStorage.set(ProductId, cartItem);
        setInCart(true);
        setShowToast(true);
      } catch (fallbackErr) {
        console.error("Even memory fallback failed:", fallbackErr);
      }
    } finally {
      setCartLoading(false);
    }
  }, [ProductData, ProductId, Quantity]);

  // ── Fire global event → FloatingCart drawer opens (no navigation) ──────────
  const handleViewCart = () => {
    window.dispatchEvent(new Event("open-cart-drawer"));
  };

  // Update quantity when in cart
  const handleQuantityChange = useCallback(
    async (newQuantity) => {
      let resolvedQty = newQuantity;
      if (typeof newQuantity === "function") {
        resolvedQty = newQuantity(Quantity);
      }
      setQuantity(resolvedQty);
      if (inCart && ProductId) {
        try {
          const item = await getCartItem(ProductId);
          if (item) {
            const updatedItem = { ...item, quantity: resolvedQty };
            await setCartItem(updatedItem);
          }
        } catch (error) {
          console.warn("Failed to update cart quantity:", error);
        }
      }
    },
    [inCart, ProductId, Quantity],
  );

  // Sync page state when cart is updated from anywhere (e.g. Cart drawer)
  useEffect(() => {
    const handleCartUpdate = async () => {
      if (ProductId) {
        try {
          const item = await getCartItem(ProductId);
          if (item) {
            setInCart(true);
            setQuantity(item.quantity || 1);
          } else {
            setInCart(false);
          }
        } catch (error) {
          console.warn("Failed to check cart item status:", error);
        }
      }
    };

    window.addEventListener("cart-updated", handleCartUpdate);
    return () => window.removeEventListener("cart-updated", handleCartUpdate);
  }, [ProductId]);

  useEffect(() => {
    const handleBotQuote = async (e) => {
      try {
        await clearAllCartItems();
        await handleAddToCart();
      } catch (err) {
        console.error(err);
      }
      setTimeout(() => {
        window.dispatchEvent(new Event("auto-request-quote"));
      }, 500);
    };

    window.addEventListener("bot-req-quote", handleBotQuote);
    return () => window.removeEventListener("bot-req-quote", handleBotQuote);
  }, [handleAddToCart]);

  useEffect(() => {
    const configScript = document.createElement("script");
    configScript.text = `window.iknow = { id: '6996bded81fca81a8f7a87cf' }`;
    document.head.appendChild(configScript);

    const widgetScript = document.createElement("script");
    widgetScript.id = "iknow-widget";
    widgetScript.src = "/assets/js/test.js";
    widgetScript.defer = true;
    document.head.appendChild(widgetScript);

    return () => {
      try {
        if (configScript && configScript.parentNode) {
          configScript.parentNode.removeChild(configScript);
        }
      } catch {}
      const existing = document.getElementById("iknow-widget");
      if (existing && existing.parentNode) {
        existing.parentNode.removeChild(existing);
      }
      try {
        const chatDiv = document.getElementById("dazh1-ChatDiv");
        if (chatDiv) chatDiv.remove();
      } catch { }
    };
  }, []);

  return (
    <>
      <Header />
      <div className="min-h-[calc(100vh_-_75px)] md:min-h-[calc(100vh_-_95px)] mt-[75px] lg:mt-[95px]">
        <div className="container mx-auto px-5">
          <div className="flex items-center gap-1 cursor-pointer font-[300] py-10">
            <p
              className="text-sm text-[#414141] hover:opacity-80"
              onClick={() => Navigate("/")}
            >
              Home
            </p>
            /
            <p
              className="text-sm text-[#414141] hover:opacity-80"
              onClick={() => Navigate("/products")}
            >
              Product
            </p>
            /
            <p className="text-sm text-[#116DAE] hover:opacity-80 font-[600]">
              {" "}
              Product Detail
            </p>
          </div>
          <section className="">
            <h2 className="text-3xl font-[500] mb-5">Explore Products</h2>
            <div className="flex gap-5 max-lg:flex-wrap sm:gap-8">
              <div className="w-full sm:min-w-[400px] sm:w-[400px] xl:min-w-[480px] xl:w-[480px] h-[290px] xl:h-[350px] bg-white flex items-center justify-center rounded-2xl">
                {ProductData?.image ? (
                  <div
                    className="w-full h-full bg-cover bg-no-repeat rounded-2xl"
                    style={{
                      backgroundImage: "url('/assets/cards-background.png')",
                    }}
                  >
                    <img
                      src={methodModel.noImg(ProductData?.image)}
                      className="rounded-2xl w-full h-full object-contain p-3"
                    // alt={ProductData?.productName || "Product image"}
                    />
                  </div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                    <svg
                      width="52"
                      height="52"
                      fill="none"
                      viewBox="0 0 64 64"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="8"
                        y="20"
                        width="48"
                        height="36"
                        rx="5"
                        fill="#EBF3FB"
                        stroke="#C8DCF0"
                        strokeWidth="2"
                      />
                      <path
                        d="M20 20V16a12 12 0 0124 0v4"
                        stroke="#C8DCF0"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <circle
                        cx="32"
                        cy="36"
                        r="6"
                        fill="#D6E9F8"
                        stroke="#116DAE"
                        strokeWidth="1.5"
                      />
                      <line
                        x1="32"
                        y1="33"
                        x2="32"
                        y2="36"
                        stroke="#116DAE"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                      <circle cx="32" cy="39.5" r="0.8" fill="#116DAE" />
                    </svg>
                    <span className="text-[11px] text-gray-400 font-medium">
                      No Image
                    </span>
                  </div>
                )}
              </div>
              <div className="w-full">
                <p className="text-sm sm:text-md text-[#414141] mb-2">
                  {ProductData?.equipmentTypeId?.title}
                </p>
                <h2
                  className="text-xl lg:text-2xl font-[500] capitalize"
                  id="productNameData"
                >
                  {ProductData?.productName}
                </h2>

                <div className="mt-4">
                  <h2 className="text-sm font-[500]">Model</h2>
                  <p className="text-sm text-gray-400">
                    {ProductData?.model || "--"}
                  </p>
                </div>

                {/* <div className="mt-4">
                  <h2 className="text-sm font-[500]">Model Number</h2>
                  {ProductData?.modelNumber?.length ? (
                    <div className="flex flex-wrap gap-y-2 gap-x-5 mt-1">
                      {ProductData.modelNumber.map((line, index) => (
                        <span
                          key={index}
                          className="text-sm text-gray-700 flex gap-1"
                        >
                          {line}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">
                      No model number available.
                    </p>
                  )}
                </div> */}

                <div className="mt-4">
                  <h2 className="text-sm lg:text-md font-[500]">Manufacture</h2>
                  <p className="text-sm text-gray-400">
                    {ProductData?.manufacturerId?.title || "--"}
                  </p>
                </div>

                {/* ── Quantity + Cart Section ── */}
                <div className="mt-4 flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <p className="text-sm font-[500] text-[#1a1a1a]">
                      Quantity
                    </p>
                    <QuantitySelector
                      quantity={Quantity}
                      setQuantity={handleQuantityChange}
                      disabled={false}
                    />
                  </div>

                  <div className="flex gap-3 items-center flex-wrap">
                    <CartButton
                      inCart={inCart}
                      onAddToCart={handleAddToCart}
                      onViewCart={handleViewCart}
                      loading={cartLoading}
                    />

                    {/* <button
                      type="button"
                      onClick={() => setIsOpen(true)}
                      className="flex items-center gap-1 text-white bg-gradient-to-br from-[#115B9D] to-[#116DAE] rounded-full hover:scale-[1.04] duration-300 transition py-2 px-7"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <g clipPath="url(#clip0_65_4)">
                          <path d="M19.7712 10.6925L17.5616 8.48273C17.415 8.33621 17.2163 8.25391 17.0091 8.25391C16.8019 8.25391 16.6032 8.33621 16.4566 8.48273L9.70422 15.2352C9.15711 15.7823 8.78773 16.4725 8.63597 17.2312L8.2691 19.0656C8.21785 19.3217 8.29804 19.5865 8.48273 19.7712C8.6307 19.9192 8.83004 20 9.03519 20C9.08613 20 9.13746 19.995 9.1884 19.9848L11.0227 19.618H11.0228C11.7815 19.4662 12.4717 19.0968 13.0188 18.5497L19.7712 11.7973C20.0763 11.4922 20.0763 10.9976 19.7712 10.6925ZM11.9139 17.4449C11.5857 17.7732 11.1715 17.9948 10.7163 18.0858L10.0311 18.2229L10.1681 17.5376C10.2592 17.0824 10.4808 16.6682 10.8091 16.34L17.0091 10.14L18.1139 11.2448L11.9139 17.4449Z" fill="white" />
                          <path d="M5.84879 18.4375H2.34375C1.91297 18.4375 1.5625 18.087 1.5625 17.6562V2.34375C1.5625 1.91297 1.91297 1.5625 2.34375 1.5625H9.375V3.90625C9.375 5.19859 10.4264 6.25 11.7188 6.25H14.0625V6.78133C14.0625 7.21281 14.4123 7.56258 14.8438 7.56258C15.2752 7.56258 15.625 7.21281 15.625 6.78133V5.46871C15.625 5.25723 15.5358 5.05445 15.3958 4.91594L10.7087 0.228828C10.5645 0.0846875 10.3626 0 10.1562 0H2.34375C1.05141 0 0 1.05141 0 2.34375V17.6562C0 18.9486 1.05141 20 2.34375 20H5.84879C6.28027 20 6.63004 19.6502 6.63004 19.2188C6.63004 18.7873 6.28023 18.4375 5.84879 18.4375ZM11.7188 4.6875C11.288 4.6875 10.9375 4.33703 10.9375 3.90625V2.66734L12.9577 4.6875H11.7188Z" fill="white" />
                          <path d="M12.5 8.28125C12.5 7.84977 12.1502 7.5 11.7188 7.5H3.90625C3.47477 7.5 3.125 7.84977 3.125 8.28125C3.125 8.71273 3.47477 9.0625 3.90625 9.0625H11.7188C12.1502 9.0625 12.5 8.71273 12.5 8.28125Z" fill="white" />
                          <path d="M3.90625 10.625C3.47477 10.625 3.125 10.9748 3.125 11.4062C3.125 11.8377 3.47477 12.1875 3.90625 12.1875H10.2188C10.6503 12.1875 11.0001 11.8377 11.0001 11.4062C11.0001 10.9748 10.6503 10.625 10.2188 10.625H3.90625Z" fill="white" />
                          <path d="M7.26453 13.75H3.90625C3.47477 13.75 3.125 14.0998 3.125 14.5312C3.125 14.9627 3.47477 15.3125 3.90625 15.3125H7.26453C7.69602 15.3125 8.04578 14.9627 8.04578 14.5312C8.04578 14.0998 7.69602 13.75 7.26453 13.75Z" fill="white" />
                        </g>
                        <defs>
                          <clipPath id="clip0_65_4">
                            <rect width="20" height="20" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      Request a Quote
                    </button> */}

                    <button
                      type="button"
                      onClick={() => setShareModalOpen(true)}
                      className="flex items-center gap-1 text-white bg-gradient-to-br from-[#42A660] to-[#20833E] rounded-full hover:scale-[1.04] duration-300 transition py-2 px-7"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="21"
                        height="18"
                        viewBox="0 0 21 18"
                        fill="none"
                      >
                        <path
                          d="M0.6875 17.8749C0.635078 17.8749 0.582141 17.8689 0.529719 17.8569C0.219484 17.783 0 17.5062 0 17.1874C0 10.9401 0.790281 5.74432 10.3125 5.50799V0.687411C10.3125 0.41413 10.4742 0.167145 10.7241 0.057661C10.9732 -0.0511359 11.2652 -0.00404223 11.4666 0.182614L20.4041 8.43261C20.5451 8.56204 20.625 8.74543 20.625 8.93741C20.625 9.1294 20.5451 9.31279 20.4041 9.44238L11.4666 17.6924C11.2659 17.879 10.9739 17.9273 10.7241 17.8173C10.4742 17.7077 10.3125 17.4607 10.3125 17.1874V12.383C3.78056 12.5394 2.57538 14.9491 1.30247 17.4949C1.18439 17.7319 0.94325 17.8749 0.6875 17.8749ZM11 10.9999C11.38 10.9999 11.6875 11.3074 11.6875 11.6874V15.617L18.9238 8.93741L11.6875 2.25783V6.18741C11.6875 6.56743 11.38 6.87491 11 6.87491C3.40519 6.87491 1.7698 9.81225 1.44822 14.5039C2.8722 12.5911 5.29925 10.9999 11 10.9999Z"
                          fill="white"
                        />
                      </svg>
                      Share
                    </button>
                  </div>
                </div>
                {/* ── End Cart Section ── */}
              </div>
            </div>

            <div className="mt-8 sm:mt-12 space-y-8">
              <div className="">
                <h2 className="text-xl font-[500] mb-3">Key Specifications</h2>
                {ProductData?.keySpecifications?.length ? (
                  <ul className="space-y-2 mt-1 ml-[18px]">
                    {ProductData.keySpecifications.map((line, index) => (
                      <li
                        key={index}
                        className="text-sm text-gray-700 list-decimal pl-3"
                      >
                        {line}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-400">
                    No key specifications available.
                  </p>
                )}
              </div>
              <div className="">
                <h2 className="text-xl font-[500] mb-3">Description</h2>
                <div className="">
                  {ProductData?.description &&
                    ProductData.description
                      .replace(/<[^>]*>?/gm, "")
                      .replace(/&nbsp;/g, " ")
                      .trim() ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: ProductData?.description,
                      }}
                    ></div>
                  ) : (
                    <p className="text-sm text-gray-400">
                      NO Description available
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid max-[480px]:grid-cols-1 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 py-12">
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <ProductSkeleton key={i} />
                ))
              ) : featuredProducts.length > 0 ? (
                featuredProducts?.map((p, i) => (
                  <ProductCard
                    key={p._id || i}
                    productData={p}
                    NavigateHandler={NavigateHandler}
                    {...p}
                  />
                ))
              ) : (
                <div className="col-span-full flex flex-col items-center justify-center py-16 text-center">
                  <svg
                    width="64"
                    height="64"
                    fill="none"
                    viewBox="0 0 64 64"
                    xmlns="http://www.w3.org/2000/svg"
                    className="mb-4"
                  >
                    <rect
                      x="8"
                      y="20"
                      width="48"
                      height="36"
                      rx="5"
                      fill="#EBF3FB"
                      stroke="#C8DCF0"
                      strokeWidth="2"
                    />
                    <path
                      d="M20 20V16a12 12 0 0124 0v4"
                      stroke="#C8DCF0"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <circle
                      cx="32"
                      cy="36"
                      r="6"
                      fill="#D6E9F8"
                      stroke="#116DAE"
                      strokeWidth="1.5"
                    />
                    <line
                      x1="32"
                      y1="33"
                      x2="32"
                      y2="36"
                      stroke="#116DAE"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <circle cx="32" cy="39.5" r="0.8" fill="#116DAE" />
                  </svg>
                  <p className="text-gray-500 font-medium">
                    No products available
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Check back soon for new arrivals.
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      <QuoteRequestModal
        open={isOpen}
        setOpen={setIsOpen}
        onSuccess={() => console.log("success")}
      />
      <ShareModal
        open={ShareModalOpen}
        url={window.location.href}
        setOpen={setShareModalOpen}
      />
      <CartToast show={showToast} onHide={() => setShowToast(false)} />

      {methodModel.TokenChecker() && <Footer />}
    </>
  );
}

// ─── Export cart helpers for use elsewhere ────────────────────────────────────
export {
  getCartItem,
  setCartItem,
  removeCartItem,
  getAllCartItems,
  clearAllCartItems,
  resetCartDatabase,
};
